import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Clé de stockage mensuelle
const getMonthlyKey = (prefix) => {
  const now = new Date();
  return `${prefix}_${now.getFullYear()}_${now.getMonth() + 1}`;
};

// Limites mensuelles
const LIMITS = {
  anonymous: { chat: 10, journal: 5, scanner: 3, mealplan: 1 },
  free: { chat: 20, journal: 5, scanner: 3, mealplan: 1 },
  pro: { chat: Infinity, journal: Infinity, scanner: Infinity, mealplan: Infinity }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authMessage, setAuthMessage] = useState(null);
  
  // Compteurs mensuels
  const [usage, setUsage] = useState(() => ({
    chat: parseInt(localStorage.getItem(getMonthlyKey('nb_chat')) || '0'),
    journal: parseInt(localStorage.getItem(getMonthlyKey('nb_journal')) || '0'),
    scanner: parseInt(localStorage.getItem(getMonthlyKey('nb_scanner')) || '0'),
    mealplan: parseInt(localStorage.getItem(getMonthlyKey('nb_mealplan')) || '0')
  }));

  // Pour compatibilité avec l'ancien code
  const messageCount = usage.chat;

  // Charger les données utilisateur
  const loadUserData = async (userId) => {
    // Charger le profil
    const { data: profileData } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (profileData) {
      setProfile(profileData);
    }

    // Charger la subscription
    const { data: subData } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .single();
    
    if (subData) {
      // Vérifier si trial expiré
      if (subData.status === 'trial' && subData.trial_end) {
        if (new Date(subData.trial_end) < new Date()) {
          subData.status = 'free';
        }
      }
      setSubscription(subData);
    } else {
      // Créer subscription free si n'existe pas
      const { data: newSub } = await supabase
        .from('subscriptions')
        .insert({ user_id: userId, status: 'free' })
        .select()
        .single();
      setSubscription(newSub || { user_id: userId, status: 'free' });
    }
  };

  // Gérer les callbacks d'auth (confirmation email, reset password, etc.)
  const handleAuthCallback = async () => {
    const hash = window.location.hash;
    
    // Vérifier s'il y a des paramètres d'erreur
    if (hash.includes('error=')) {
      const params = new URLSearchParams(hash.substring(1));
      const error = params.get('error');
      const errorDesc = params.get('error_description');
      
      console.error('Auth callback error:', error, errorDesc);
      
      // Nettoyer l'URL
      window.history.replaceState(null, '', window.location.pathname);
      
      if (errorDesc?.includes('confirm')) {
        setAuthMessage({ type: 'error', text: 'Erreur lors de la confirmation. Le lien a peut-être expiré.' });
      }
      return;
    }
    
    // Vérifier s'il y a un token d'accès (magic link, confirmation, etc.)
    if (hash.includes('access_token=') || hash.includes('type=')) {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session error:', error);
          setAuthMessage({ type: 'error', text: 'Erreur de connexion. Veuillez réessayer.' });
        } else if (data.session) {
          // Vérifier le type d'événement
          const params = new URLSearchParams(hash.substring(1));
          const type = params.get('type');
          
          if (type === 'recovery') {
            setAuthMessage({ type: 'info', text: 'Vous pouvez maintenant changer votre mot de passe dans votre profil.' });
          } else if (type === 'email_change') {
            setAuthMessage({ type: 'success', text: 'Votre adresse email a été mise à jour avec succès !' });
          } else if (type === 'signup') {
            setAuthMessage({ type: 'success', text: 'Email confirmé ! Bienvenue sur MonCoach.' });
          } else if (type === 'magiclink') {
            setAuthMessage({ type: 'success', text: 'Connexion réussie !' });
          }
        }
        
        // Nettoyer l'URL
        window.history.replaceState(null, '', window.location.pathname);
      } catch (e) {
        console.error('Auth callback error:', e);
      }
    }
  };

  useEffect(() => {
    // Gérer les callbacks d'auth au chargement
    handleAuthCallback();
    
    // Vérifier la session au démarrage
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        loadUserData(session.user.id);
      }
      setLoading(false);
    });

    // Écouter les changements
    const { data: { subscription: authListener } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth event:', event);
        
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          loadUserData(session.user.id);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          setSubscription(null);
        } else if (event === 'USER_UPDATED') {
          // Email changé ou profil mis à jour
          if (session?.user) {
            setUser(session.user);
            loadUserData(session.user.id);
          }
        } else if (event === 'PASSWORD_RECOVERY') {
          setAuthMessage({ type: 'info', text: 'Vous pouvez maintenant changer votre mot de passe.' });
        }
      }
    );

    return () => authListener.unsubscribe();
  }, []);

  // Effacer le message après 5 secondes
  useEffect(() => {
    if (authMessage) {
      const timer = setTimeout(() => setAuthMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [authMessage]);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { success: false, error: error.message };
    // Les compteurs restent - ils sont mensuels
    return { success: true, user: data.user };
  };

  const register = async (userData) => {
    const { data, error } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
      options: {
        data: { name: userData.name }
      }
    });

    if (error) return { success: false, error: error.message };

    if (data.user) {
      // Créer profil
      await supabase.from('profiles').upsert({
        id: data.user.id,
        email: userData.email,
        name: userData.name,
        goal: userData.goal
      }, { onConflict: 'id' });

      // Créer subscription
      await supabase.from('subscriptions').upsert({
        user_id: data.user.id,
        status: 'free'
      }, { onConflict: 'user_id' });
      
      // Les compteurs restent - ils sont mensuels
    }

    return { success: true, user: data.user };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setSubscription(null);
    // Reset pour revoir la landing à la prochaine visite
    localStorage.removeItem('nb_seen_landing');
    localStorage.removeItem('nb_landing_seen');
  };

  const updateProfile = async (updates) => {
    if (!user) return;
    
    // Mettre à jour localement
    setProfile(prev => ({ ...prev, ...updates }));
    
    // Sauvegarder en DB
    await supabase
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', user.id);
  };

  const startTrial = async () => {
    if (!user) return false;
    
    const trialEnd = new Date();
    trialEnd.setDate(trialEnd.getDate() + 7);

    setSubscription({
      user_id: user.id,
      status: 'trial',
      trial_start: new Date().toISOString(),
      trial_end: trialEnd.toISOString()
    });

    await supabase
      .from('subscriptions')
      .upsert({
        user_id: user.id,
        status: 'trial',
        trial_start: new Date().toISOString(),
        trial_end: trialEnd.toISOString()
      }, { onConflict: 'user_id' });

    return true;
  };

  // Obtenir le type d'utilisateur
  const getUserType = () => {
    const status = getSubscriptionStatus();
    if (status === 'pro' || status === 'trial') return 'pro';
    if (user) return 'free';
    return 'anonymous';
  };

  // Obtenir la limite pour une feature
  const getLimit = (feature) => {
    return LIMITS[getUserType()][feature] || 0;
  };

  // Vérifier si peut utiliser une feature
  const canUse = (feature) => {
    const limit = getLimit(feature);
    if (limit === Infinity) return true;
    return usage[feature] < limit;
  };

  // Obtenir le nombre restant
  const getRemaining = (feature) => {
    const limit = getLimit(feature);
    if (limit === Infinity) return Infinity;
    return Math.max(0, limit - usage[feature]);
  };

  // Incrémenter l'usage d'une feature
  const incrementUsage = (feature) => {
    const newCount = usage[feature] + 1;
    setUsage(prev => ({ ...prev, [feature]: newCount }));
    localStorage.setItem(getMonthlyKey(`nb_${feature}`), newCount.toString());
    return newCount;
  };

  // Pour compatibilité : anciennes fonctions
  const incrementMessageCount = () => incrementUsage('chat');
  
  const canSendMessage = () => canUse('chat');

  const getSubscriptionStatus = () => {
    if (!subscription) return 'free';
    
    // Vérifier si annulé
    if (subscription.status === 'canceled' || subscription.status === 'cancelled') {
      return 'cancelled';
    }
    
    // Vérifier si va être annulé à la fin de la période
    if (subscription.cancel_at_period_end) {
      return 'cancelled';
    }
    
    // Vérifier trial
    if (subscription.status === 'trial') {
      if (new Date(subscription.trial_end) > new Date()) return 'trial';
      return 'free';
    }
    
    return subscription.status;
  };

  const isAuthenticated = !!user;

  const clearAuthMessage = () => setAuthMessage(null);

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      subscription,
      loading,
      messageCount,
      usage,
      authMessage,
      login,
      register,
      logout,
      updateProfile,
      startTrial,
      incrementMessageCount,
      incrementUsage,
      canSendMessage,
      canUse,
      getLimit,
      getRemaining,
      getUserType,
      getSubscriptionStatus,
      isAuthenticated,
      clearAuthMessage
    }}>
      {children}
    </AuthContext.Provider>
  );
};
